import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { BookOpen, Search, Trash2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PortfolioPage() {
  const [clips, setClips] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOutlet, setFilterOutlet] = useState('all');

  useEffect(() => {
    fetchClips();
  }, []);

  const fetchClips = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/clips`);
      setClips(response.data);
    } catch (error) {
      console.error('Failed to fetch clips:', error);
    }
  };

  const handleDelete = async (clip) => {
    if (!window.confirm('Are you sure you want to delete this clip?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/clips/${clip.date}/${encodeURIComponent(clip.title)}`);
      toast.success('Clip deleted');
      fetchClips();
    } catch (error) {
      toast.error('Failed to delete clip');
    }
  };

  const filteredClips = clips.filter(clip => {
    const matchesSearch = clip.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         clip.content.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesOutlet = filterOutlet === 'all' || clip.outlet === filterOutlet;
    return matchesSearch && matchesOutlet;
  });

  const outlets = ['all', ...new Set(clips.map(c => c.outlet))];

  return (
    <div className="p-4 sm:p-8 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-8 sm:mb-12">
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-2">Clip Portfolio</h1>
        <p className="text-lg text-muted-foreground font-['IBM_Plex_Sans']">
          Your saved work and best pieces
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-card border border-border p-6 rounded-sm">
          <div className="flex items-center gap-2 mb-2">
            <BookOpen className="w-5 h-5 text-primary" />
            <h3 className="text-sm font-bold tracking-tight uppercase tracking-widest font-['IBM_Plex_Sans']">
              Total Clips
            </h3>
          </div>
          <p className="text-4xl font-bold tracking-tight">{clips.length}</p>
        </div>

        <div className="bg-card border border-border p-6 rounded-sm">
          <h3 className="text-sm font-bold tracking-tight uppercase tracking-widest mb-2 font-['IBM_Plex_Sans']">
            By Type
          </h3>
          <div className="text-sm space-y-1 font-['IBM_Plex_Sans']">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Briefs:</span>
              <span className="font-medium">{clips.filter(c => c.type === 'brief').length}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Analysis:</span>
              <span className="font-medium">{clips.filter(c => c.type === 'analysis').length}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Other:</span>
              <span className="font-medium">{clips.filter(c => !['brief', 'analysis'].includes(c.type)).length}</span>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border p-6 rounded-sm">
          <h3 className="text-sm font-bold tracking-tight uppercase tracking-widest mb-2 font-['IBM_Plex_Sans']">
            Outlets Covered
          </h3>
          <p className="text-4xl font-bold tracking-tight">
            {new Set(clips.map(c => c.outlet)).size}
          </p>
        </div>
      </div>

      {/* Search & Filter */}
      <div className="mb-6 flex gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            data-testid="input-search-clips"
            type="text"
            placeholder="Search clips..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 font-['IBM_Plex_Sans']"
          />
        </div>
        <select
          data-testid="select-filter-outlet"
          value={filterOutlet}
          onChange={(e) => setFilterOutlet(e.target.value)}
          className="px-4 py-2 bg-card border border-border rounded-sm font-['IBM_Plex_Sans'] text-sm"
        >
          {outlets.map(outlet => (
            <option key={outlet} value={outlet}>
              {outlet === 'all' ? 'All Outlets' : outlet}
            </option>
          ))}
        </select>
      </div>

      {/* Clips Grid */}
      {filteredClips.length === 0 ? (
        <div className="text-center py-16 bg-card border border-border rounded-sm">
          <BookOpen className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground font-['IBM_Plex_Sans']">
            {clips.length === 0 
              ? 'No clips saved yet. Save your best work from the workspace!'
              : 'No clips match your search.'
            }
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredClips.map((clip, index) => (
            <div
              key={index}
              data-testid={`clip-${index}`}
              className="bg-card border border-border p-6 rounded-sm hover:border-primary/50 transition-colors"
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="text-xl font-bold tracking-tight mb-1">{clip.title}</h3>
                  <div className="flex gap-3 text-sm text-muted-foreground font-['IBM_Plex_Sans']">
                    <span>{clip.outlet}</span>
                    <span>•</span>
                    <span>{clip.date}</span>
                    <span>•</span>
                    <span className="capitalize">{clip.type}</span>
                  </div>
                </div>
                <Button
                  data-testid={`btn-delete-clip-${index}`}
                  onClick={() => handleDelete(clip)}
                  variant="ghost"
                  size="sm"
                  className="text-muted-foreground hover:text-destructive"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
              
              <div className="prose max-w-none">
                <p className="text-foreground font-['Source_Serif_4'] leading-relaxed whitespace-pre-wrap">
                  {clip.content}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

